import type { CantonClient, SpliceProvider } from "@sigilry/dapp";
import getProperty from "lodash/get";
import type { ConnectorID } from "../types/wallet";
import { walletIconBaseUrl } from "../wallets";

export interface InjectedCantonWalletConfig {
  connectorId: ConnectorID;
  name: string;
  icon?: string;
  /** Dot-path to the injected CIP-103 provider on `window` (e.g. `nightly.canton`). */
  windowPath: string;
}

/**
 * CIP-103 wallets that expose their provider as an injected `window` global
 * instead of participating in the `canton:announceProvider` discovery protocol.
 * Add an entry to support a new one.
 */
export const INJECTED_CANTON_WALLETS: InjectedCantonWalletConfig[] = [
  {
    connectorId: "nightly",
    name: "Nightly",
    icon: `${walletIconBaseUrl}/nightly.webp`,
    windowPath: "nightly.canton",
  },
];

export interface DiscoveredInjectedCantonWallet {
  config: InjectedCantonWalletConfig;
  provider: SpliceProvider;
}

/**
 * Adapts an injected CIP-103 `SpliceProvider` (the normative `request` API) to
 * the {@link CantonClient} surface consumed by `CantonBrowserConnector`, so
 * injected wallets reuse the same connector as announce-discovered ones.
 */
export function cantonClientFromSpliceProvider(
  provider: SpliceProvider
): CantonClient {
  return {
    status: () => provider.request({ method: "status" }),
    connect: () => provider.request({ method: "connect" }),
    disconnect: () => provider.request({ method: "disconnect" }),
    isConnected: () => provider.request({ method: "isConnected" }),
    getActiveNetwork: () => provider.request({ method: "getActiveNetwork" }),
    listAccounts: () => provider.request({ method: "listAccounts" }),
    getPrimaryAccount: () => provider.request({ method: "getPrimaryAccount" }),
    prepareExecute: (params) =>
      provider.request({ method: "prepareExecute", params }),
    prepareExecuteAndWait: (params) =>
      provider.request({ method: "prepareExecuteAndWait", params }),
    signMessage: (params) => provider.request({ method: "signMessage", params }),
    ledgerApi: (params) => provider.request({ method: "ledgerApi", params }),
  };
}

/**
 * Detect installed injected Canton wallets by walking each registered
 * `windowPath`, mirroring the `definedInWindow` install check used for other
 * non-autodiscovered wallets.
 */
export function discoverInjectedCantonWallets(
  win?: Window
): DiscoveredInjectedCantonWallet[] {
  const source = win ?? (typeof window !== "undefined" ? window : undefined);
  if (!source) return [];

  return INJECTED_CANTON_WALLETS.flatMap((config) => {
    const provider = getProperty(source, config.windowPath) as
      | SpliceProvider
      | undefined;
    if (!provider || typeof provider.request !== "function") return [];
    return [{ config, provider }];
  });
}
